import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { localeInputSchema, type LocaleInput, type LocaleView } from '@agency/contracts';
import { Badge, Button, Drawer, EmptyState, Notice, Skeleton, TextField, useToast, scrollRegion } from '@agency/ui';
import { ApiClientError, get, post, put } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { QueryError } from '../../lib/query-state.tsx';
import { CheckField, ConfirmDrawer, errText, issueMap, orUndef, serverErrors, type Errors } from './common.tsx';

const toInput = (l: LocaleView, enabled = l.enabled): LocaleInput => ({ code: l.code, name: l.name, countryCode: l.countryCode, hreflang: l.hreflang, urlPrefix: l.urlPrefix, currency: l.currency, timezone: l.timezone, enabled });
type Editing = { mode: 'create' } | { mode: 'edit'; locale: LocaleView };

/** Locale caches are shared with the page wizard and translation pickers, so every change refreshes them all. */
function useLocaleRefresh() {
  const qc = useQueryClient();
  return () => Promise.all([['site-locales'], ['locale-options'], ['locales'], ['seo']].map((queryKey) => qc.invalidateQueries({ queryKey })));
}

export function LanguagesTab() {
  const { can } = useAuth();
  const toast = useToast();
  const refresh = useLocaleRefresh();
  const manage = can('settings.manage');
  const [editing, setEditing] = useState<Editing | null>(null);
  const [defaulting, setDefaulting] = useState<LocaleView | null>(null);
  const [defaultError, setDefaultError] = useState<string | null>(null);
  const q = useQuery<LocaleView[], ApiClientError>({ queryKey: ['site-locales'], queryFn: () => get('/admin/site/locales') });

  const toggle = useMutation({
    mutationFn: (v: { locale: LocaleView; enabled: boolean }) => put<LocaleView>('/admin/site/locales', toInput(v.locale, v.enabled)),
    onSuccess: async (_r, v) => { await refresh(); toast({ tone: 'success', message: `${v.locale.name} ${v.enabled ? 'enabled' : 'disabled'}.` }); },
    onError: () => void refresh(),
  });
  const makeDefault = useMutation({
    mutationFn: (l: LocaleView) => post(`/admin/site/locales/${encodeURIComponent(l.code)}/make-default`),
    onSuccess: async (_r, l) => { await refresh(); toast({ tone: 'success', message: `${l.name} is now the default language.` }); setDefaulting(null); },
    onError: (e) => setDefaultError(errText(e)),
  });

  return (
    <section className="panel" aria-labelledby="lang-h">
      <div className="panel__head">
        <div><h2 id="lang-h">Languages</h2><p className="ag-muted">Languages a page can be translated into. Each non-default language has its own URL prefix. A language with published pages cannot be disabled.</p></div>
        {manage ? <Button variant="primary" icon="plus" onClick={() => setEditing({ mode: 'create' })}>Add language</Button> : null}
      </div>
      {toggle.isError ? <Notice tone="error" title="Could not change the language">{errText(toggle.error)}</Notice> : null}
      {q.isLoading ? <Skeleton width="100%" height={160} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : q.data!.length === 0 ? <EmptyState title="No languages" icon="globe" /> : (
        <div className="ag-table-wrap" {...scrollRegion('Languages')}>
          <table className="ag-table">
            <caption className="ag-visually-hidden">Languages</caption>
            <thead><tr><th scope="col">Language</th><th scope="col">URL prefix</th><th scope="col">hreflang</th><th scope="col">Currency</th><th scope="col">Status</th><th scope="col">Enabled</th>{manage ? <th scope="col"><span className="ag-visually-hidden">Actions</span></th> : null}</tr></thead>
            <tbody>
              {q.data!.map((l) => (
                <tr key={l.code}>
                  <td><span className="ag-cell-2"><span>{l.name}</span><span className="ag-mono">{l.code}</span></span></td>
                  <td className="ag-mono">{l.urlPrefix}</td>
                  <td className="ag-mono">{l.hreflang}</td>
                  <td>{l.currency ?? <span className="ag-muted">Not set</span>}</td>
                  <td>{l.isDefault ? <Badge tone="accent">Default</Badge> : <Badge tone={l.enabled ? 'success' : 'neutral'}>{l.enabled ? 'Enabled' : 'Disabled'}</Badge>}</td>
                  <td>
                    <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                      <input type="checkbox" checked={l.enabled} disabled={!manage || l.isDefault || (toggle.isPending && toggle.variables?.locale.code === l.code)} onChange={(e) => toggle.mutate({ locale: l, enabled: e.target.checked })} />
                      <span className="ag-visually-hidden">Enabled: {l.name}</span><span aria-hidden="true">{l.enabled ? 'On' : 'Off'}</span>
                    </label>
                  </td>
                  {manage ? (
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button size="sm" onClick={() => setEditing({ mode: 'edit', locale: l })} aria-label={`Edit ${l.name}`}>Edit</Button>{' '}
                      {!l.isDefault ? <Button size="sm" variant="ghost" disabled={!l.enabled} title={l.enabled ? undefined : 'Enable the language first'} onClick={() => { setDefaultError(null); setDefaulting(l); }} aria-label={`Make ${l.name} the default language`}>Make default…</Button> : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!manage ? <p className="ag-muted">You can view languages but not change them.</p> : null}
      <Drawer open={!!editing} onClose={() => setEditing(null)} title={editing?.mode === 'edit' ? `Edit ${editing.locale.name}` : 'Add language'} footer={null}>
        {editing ? <LocaleForm key={editing.mode === 'edit' ? editing.locale.code : 'new'} editing={editing} onDone={async () => { await refresh(); setEditing(null); }} onClose={() => setEditing(null)} /> : null}
      </Drawer>
      <ConfirmDrawer open={!!defaulting} title="Change the default language?" confirmLabel="Make default" pending={makeDefault.isPending} error={defaultError} onClose={() => setDefaulting(null)} onConfirm={() => defaulting && makeDefault.mutate(defaulting)}>
        <p>{defaulting?.name} will become the default language. The default language has no URL prefix, so changing it changes public URLs across the site.</p>
      </ConfirmDrawer>
    </section>
  );
}

function LocaleForm({ editing, onDone, onClose }: { editing: Editing; onDone: () => Promise<void>; onClose: () => void }) {
  const toast = useToast();
  const l = editing.mode === 'edit' ? editing.locale : null;
  const [v, setV] = useState({ code: l?.code ?? '', name: l?.name ?? '', urlPrefix: l?.urlPrefix ?? '', hreflang: l?.hreflang ?? '', countryCode: l?.countryCode ?? '', currency: l?.currency ?? '', timezone: l?.timezone ?? '', enabled: l?.enabled ?? false });
  const [errors, setErrors] = useState<Errors>({});
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: LocaleInput) => put<LocaleView>('/admin/site/locales', body),
    onSuccess: async (r) => { toast({ tone: 'success', message: `${r.name} saved.` }); await onDone(); },
    onError: (e) => { setErrors(serverErrors(e)); setError(errText(e)); },
  });
  const f = (k: keyof typeof v) => ({ value: v[k] as string, error: errors[k], onChange: (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value }) });
  function submit() {
    setError(null);
    const p = localeInputSchema.safeParse({ code: v.code, name: v.name, urlPrefix: orUndef(v.urlPrefix), hreflang: orUndef(v.hreflang), countryCode: orUndef(v.countryCode), currency: orUndef(v.currency), timezone: orUndef(v.timezone), enabled: v.enabled });
    if (!p.success) { setErrors(issueMap(p.error.issues)); return; }
    setErrors({});
    save.mutate(p.data);
  }
  return (
    <form noValidate className="formgrid" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      {error ? <Notice tone="error" title="Could not save the language">{error}</Notice> : null}
      {l?.isDefault ? <Notice tone="info" title="Default language" live={false}>The default language is always enabled.</Notice> : null}
      <TextField data-autofocus={l ? undefined : true} label="Code" required readOnly={!!l} hint={l ? 'The code cannot be changed.' : 'A language like en or de, optionally with a region like de-CH'} {...f('code')} />
      <TextField data-autofocus={l ? true : undefined} label="Name" required {...f('name')} />
      <TextField label="URL prefix" hint={l ? undefined : 'Leave blank to use the lowercase code.'} {...f('urlPrefix')} />
      <TextField label="hreflang" hint={l ? undefined : 'Leave blank to use the code.'} {...f('hreflang')} />
      <TextField label="Country code" hint="Two capital letters, for example DE" {...f('countryCode')} />
      <TextField label="Currency" hint="Three capital letters, for example EUR" {...f('currency')} />
      <TextField label="Timezone" hint="For example Europe/Berlin" {...f('timezone')} />
      <CheckField label="Enabled" checked={v.enabled} disabled={!!l?.isDefault} hint="Only enabled languages can be used for pages and markets." onChange={(enabled) => setV({ ...v, enabled })} />
      <div className="actions__row">
        <Button type="submit" variant="primary" loading={save.isPending}>{l ? 'Save changes' : 'Add language'}</Button>
        <Button onClick={onClose}>Cancel</Button>
      </div>
    </form>
  );
}
